
import { useState, useEffect } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    motivo: '',
    mensaje: '',
    privacidad: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [selectedDoctor, setSelectedDoctor] = useState('');

  useEffect(() => {
    // Verificar si hay un doctor preseleccionado en la URL
    const urlParams = new URLSearchParams(window.location.search);
    const doctorParam = urlParams.get('doctor');
    if (doctorParam) {
      setSelectedDoctor(decodeURIComponent(doctorParam));
      setFormData(prev => ({ ...prev, motivo: `Consulta con ${decodeURIComponent(doctorParam)}` }));
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: checkbox.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    if (!formData.nombre.trim()) return 'El nombre es requerido';
    if (!formData.telefono.trim()) return 'El teléfono es requerido';
    if (!formData.email.trim()) return 'El email es requerido';
    if (!/\S+@\S+\.\S+/.test(formData.email)) return 'El email no es válido';
    if (!formData.motivo.trim()) return 'El motivo de consulta es requerido';
    if (formData.mensaje.length > 500) return 'El mensaje no puede exceder 500 caracteres';
    if (!formData.privacidad) return 'Debe aceptar el aviso de privacidad';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const subject = 'Nueva solicitud de contacto – UroMás';
      const body = `Nombre: ${formData.nombre}\n` +
        `Teléfono: ${formData.telefono}\n` +
        `Email: ${formData.email}\n` +
        `Motivo: ${formData.motivo}\n` +
        `Mensaje: ${formData.mensaje || '(sin mensaje)'}\n` +
        `Aceptó aviso de privacidad: ${formData.privacidad ? 'Sí' : 'No'}`;

      const mailtoUrl = `mailto:omarsol@icloud.com?cc=${encodeURIComponent('juanpablo010320@gmail.com')}` +
        `&subject=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(body)}`;

      // Abrir el cliente de correo del usuario
      window.location.href = mailtoUrl;

      // Consideramos el flujo como exitoso tras abrir el cliente de correo
      setSubmitStatus('success');
      setFormData({
        nombre: '',
        telefono: '',
        email: '',
        motivo: '',
        mensaje: '',
        privacidad: false
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppContact = () => {
    const message = encodeURIComponent(
      `Hola, me interesa agendar una consulta urológica.\n\nNombre: ${formData.nombre}\nTeléfono: ${formData.telefono}\nMotivo: ${formData.motivo || 'Consulta general'}`
    );
    window.open(`https://wa.me/523318648274?text=${message}`, '_blank');
  };

  return (
    <section id="contacto" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[#111317] mb-4">
            Agenda tu Consulta
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Contáctanos para programar tu cita con nuestros especialistas en urología
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Formulario */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <form id="contacto-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              {selectedDoctor && (
                <div className="bg-[#1B84FF]/10 border border-[#1B84FF]/20 rounded-lg p-4 mb-6">
                  <p className="text-[#0B2A4A] font-medium">
                    <i className="ri-user-line mr-2"></i>
                    Solicitud de consulta con: {selectedDoctor}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B84FF] focus:border-transparent transition-all text-sm"
                    placeholder="Ingresa tu nombre completo"
                  />
                </div>

                <div>
                  <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B84FF] focus:border-transparent transition-all text-sm"
                    placeholder="(33) 1234-5678"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Correo electrónico *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B84FF] focus:border-transparent transition-all text-sm"
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <label htmlFor="motivo" className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo de consulta *
                </label>
                <select
                  id="motivo"
                  name="motivo"
                  value={formData.motivo}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B84FF] focus:border-transparent transition-all text-sm bg-white"
                >
                  <option value="">Selecciona el motivo de tu consulta</option>
                  <option value="Consulta general">Consulta general</option>
                  <option value="Problemas de próstata">Problemas de próstata</option>
                  <option value="Cálculos renales">Cálculos renales</option>
                  <option value="Disfunción eréctil">Disfunción eréctil</option>
                  <option value="Infecciones urinarias">Infecciones urinarias</option>
                  <option value="Vasectomía">Vasectomía</option>
                  <option value="Chequeo preventivo">Chequeo preventivo</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>

              <div>
                <label htmlFor="mensaje" className="block text-sm font-medium text-gray-700 mb-2">
                  Mensaje adicional
                  <span className="text-gray-500 text-xs ml-1">(máximo 500 caracteres)</span>
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B84FF] focus:border-transparent transition-all text-sm resize-none"
                  placeholder="Describe brevemente tu consulta o síntomas..."
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.mensaje.length}/500 caracteres
                </div>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="privacidad"
                  name="privacidad"
                  checked={formData.privacidad}
                  onChange={handleInputChange}
                  required
                  className="mt-1 mr-3 h-4 w-4 text-[#1B84FF] focus:ring-[#1B84FF] border-gray-300 rounded"
                />
                <label htmlFor="privacidad" className="text-sm text-gray-700">
                  Acepto el <a href="#" className="text-[#1B84FF] hover:underline">aviso de privacidad</a> y 
                  autorizo el uso de mis datos para el contacto y seguimiento médico. *
                </label>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 flex items-center">
                    <i className="ri-check-circle-line mr-2"></i>
                    ¡Mensaje enviado correctamente! Nos pondremos en contacto contigo pronto.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 flex items-center">
                    <i className="ri-error-warning-line mr-2"></i>
                    Hubo un error al enviar el mensaje. Por favor, intenta nuevamente.
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-[#0B2A4A] text-white py-3 px-6 rounded-lg hover:bg-[#1B84FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Enviar Solicitud
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleWhatsAppContact}
                  className="flex-1 bg-[#25D366] text-white py-3 px-6 rounded-lg hover:bg-[#128C7E] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </button>
              </div>
            </form>
          </div>

          {/* Información de contacto */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-[#111317] mb-6">
                Información de Contacto
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#1B84FF]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-[#1B84FF] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#111317] mb-1">Teléfono</h4>
                    <p className="text-gray-600">+52 33 1864 8274</p>
                    <p className="text-gray-600">+52 33 1864 8274</p>
                    <p className="text-sm text-gray-500">Lunes a Viernes: 8:00 AM - 6:00 PM</p>
                  </div>
                </div>

                {/* Bloque de WhatsApp removido a solicitud */}

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#1B84FF]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-mail-line text-[#1B84FF] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#111317] mb-1">Email</h4>
                    <p className="text-gray-600">info@centrourologicoguadalajara.com</p>
                    <p className="text-sm text-gray-500">Respuesta en 24 horas</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#1B84FF]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-time-line text-[#1B84FF] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#111317] mb-1">Horarios</h4>
                    <p className="text-gray-600">Lunes a Viernes: 8:00 AM - 6:00 PM</p>
                    <p className="text-gray-600">Sábados: 9:00 AM - 2:00 PM</p>
                    <p className="text-sm text-[#1B84FF]">Urgencias 24/7</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#0B2A4A] to-[#1B84FF] rounded-2xl shadow-lg p-8 text-white">
              <h3 className="text-xl font-bold mb-4">
                <i className="ri-shield-check-line mr-2"></i>
                Tu privacidad es nuestra prioridad
              </h3>
              <p className="text-blue-100 leading-relaxed">
                Todos nuestros servicios se manejan con total confidencialidad. 
                Tu información médica y personal está protegida bajo estrictos 
                protocolos de privacidad médica.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
